import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { API_ENDPOINTS, UNIVERSITY_CACHE } from '../../config/api';
import { getUniversityLogo } from '../../utils/universityLogos';
import { SUPPORTED_CITIES, matchMajorCities, matchSupportedCityFromInput } from '../../config/cities';
import {
    getStateByUniversityId,
    matchSupportedStateFromInput,
    matchSupportedStates,
    shortCityLabel,
} from '../../config/states';
import './UniversitySearch.css';

interface University {
    id: number;
    name: string;
    abbreviation: string;
    logo_url?: string;
    listing_count?: number;
    isCity?: boolean;      // true when this entry represents a city alias
    isState?: boolean;     // true when this entry represents a whole state
    cityLabel?: string;    // display label e.g. "Tampa, FL"
    unsupported?: boolean; // true when shown for discovery but not searchable yet
}

const locationFromNegativeId = (valueInt: number): University | null => {
    const state = getStateByUniversityId(valueInt);
    if (state) {
        return {
            id: valueInt,
            name: state.label,
            abbreviation: state.abbreviation,
            isState: true,
            cityLabel: state.label,
        };
    }
    const city = SUPPORTED_CITIES.find((c) => c.universityId === valueInt);
    if (city) {
        return {
            id: valueInt,
            name: city.label,
            abbreviation: '',
            isCity: true,
            cityLabel: city.label,
        };
    }
    return null;
};

interface UniversitySearchProps {
    value: string;
    onChange: (universityId: string) => void;
    onInputChange?: (value: string) => void;
    onSearch?: () => void; // Callback when Enter is pressed and university is selected
    onValidationChange?: (isValid: boolean) => void; // Callback when validation state changes
    placeholder?: string;
    className?: string;
    label?: string;
    disableCapsuleClick?: boolean; // When true, removes the click-to-clear behavior
    supportedUniversityIds?: number[]; // Optional list of supported university IDs to filter suggestions
    onUnsupportedSearch?: (query: string) => void; // Callback when user searches for something that doesn't match a supported university
    dropdownOnly?: boolean; // When true, behaves like a select menu/dropdown instead of a search input
    disableCapsule?: boolean; // When true, displays selected value in input instead of as a capsule
    showCities?: boolean; // When true, inject Tampa/NYC city options at the top of suggestions
}

const STATIC_SUPPORTED_UNIVERSITIES: University[] = [
    { id: 10, name: "University of South Florida", abbreviation: "USF", logo_url: "https://d3o9w5vamj1hnt.cloudfront.net/static/media/usf.45ddbd82d509440548f9.png" },
    { id: 123, name: "New York University", abbreviation: "NYU", logo_url: "" }
];

// Industry standard: Cache the university list globally to avoid redundant fetches across component re-renders
let globalUniversityCache: University[] | null = [...STATIC_SUPPORTED_UNIVERSITIES];

const UniversitySearch: React.FC<UniversitySearchProps> = ({
    value,
    onChange,
    onInputChange,
    onSearch,
    onValidationChange,
    placeholder = "Search University e.g. USF",
    className = "",
    label,
    disableCapsuleClick = false,
    supportedUniversityIds,
    onUnsupportedSearch,
    dropdownOnly = false,
    disableCapsule = false,
    showCities = false,
}) => {
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [suggestions, setSuggestions] = useState<University[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedUniversity, setSelectedUniversity] = useState<University | null>(null);
    const [dynamicFontSize, setDynamicFontSize] = useState<number | null>(null);
    const [containerWidth, setContainerWidth] = useState<number | null>(null);
    const [fontReadyTick, setFontReadyTick] = useState(0);
    const [isMobile, setIsMobile] = useState<boolean>(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
    /** Use university abbreviations in the browse capsule below this width. */
    const [useCompactCapsuleLabel, setUseCompactCapsuleLabel] = useState<boolean>(
        typeof window !== 'undefined' ? window.innerWidth <= 1100 : false
    );
    const [isFocused, setIsFocused] = useState(false);

    const searchRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputContainerRef = useRef<HTMLDivElement>(null);
    const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const measureCanvasRef = useRef<HTMLCanvasElement | null>(null);

    const isHomePage =
        className.includes('home-page-university-search') ||
        className.includes('home-landing-university-search');

    // Browse results filters: icon-only location capsule (search + pin/logo)
    const isBrowsePageSearch = className.includes('home-page-university-search');

    const isSignupPage = className.includes('signup-page-university-search');
    const isEditProfilePage = className.includes('edit-profile-university-search');

    // Fetch universities on mount to get selected university name
    useEffect(() => {
        const fetchUniversities = async () => {
            try {
                // Ensure cache is initialized
                let data = globalUniversityCache;
                if (!data || data.length === 0) {
                    data = [...STATIC_SUPPORTED_UNIVERSITIES];
                    globalUniversityCache = data;
                }
                
                // Try to load fresh cache from localStorage
                const cached = localStorage.getItem(UNIVERSITY_CACHE.KEY);
                let hasFreshCache = false;
                if (cached) {
                    try {
                        const { timestamp, universities } = JSON.parse(cached);
                        if (Date.now() - timestamp < UNIVERSITY_CACHE.EXPIRY) {
                            data = universities;
                            globalUniversityCache = data;
                            hasFreshCache = true;
                        }
                    } catch (e) {
                        localStorage.removeItem(UNIVERSITY_CACHE.KEY);
                    }
                }

                // If value is set, configure selected university/city/state immediately
                if (value) {
                    const valueInt = parseInt(value, 10);
                    if (valueInt < 0) {
                        const location = locationFromNegativeId(valueInt);
                        if (location) {
                            setSelectedUniversity(location);
                            setSearchQuery(location.name);
                        }
                    } else if (data) {
                        const uni = data.find((u: University) => u.id.toString() === value);
                        if (uni) {
                            setSelectedUniversity(uni);
                            setSearchQuery(uni.name);
                        }
                    }
                }

                // If localStorage is stale/missing, run background fetch to refresh it (does not block search)
                if (!hasFreshCache) {
                    try {
                        const response = await fetch(API_ENDPOINTS.UNIVERSITIES.LIST);
                        const resJson = await response.json();
                        const serverData = resJson.data || [];
                        if (serverData && serverData.length > 0) {
                            globalUniversityCache = serverData;
                            localStorage.setItem(UNIVERSITY_CACHE.KEY, JSON.stringify({
                                timestamp: Date.now(),
                                universities: serverData
                            }));
                            // Update display name if value is active
                            if (value) {
                                const valueInt = parseInt(value, 10);
                                if (valueInt < 0) {
                                    const location = locationFromNegativeId(valueInt);
                                    if (location) {
                                        setSelectedUniversity(location);
                                        setSearchQuery(location.name);
                                    }
                                } else {
                                    const uni = serverData.find((u: University) => u.id.toString() === value);
                                    if (uni) {
                                        setSelectedUniversity(uni);
                                        setSearchQuery(uni.name);
                                    }
                                }
                            }
                        }
                    } catch (fetchError) {
                        console.error('Background university fetch failed, using fallback list:', fetchError);
                    }
                }
            } catch (error) {
                console.error('Error in university initialization:', error);
            }
        };
        fetchUniversities();
    }, [value]);

    // Recalculate after fonts load (important for accurate width measurement).
    useEffect(() => {
        let cancelled = false;
        const fonts = (document as unknown as { fonts?: FontFaceSet }).fonts;
        if (!fonts?.ready) return;

        fonts.ready
            .then(() => {
                if (!cancelled) setFontReadyTick((t) => t + 1);
            })
            .catch(() => {
                // ignore
            });

        return () => {
            cancelled = true;
        };
    }, []);

    // Debounced search
    const performSearch = useCallback(async (query: string) => {
        try {
            const trimmed = query.trim();

            // Never open the dropdown until the user has typed something
            if (!trimmed) {
                setSuggestions([]);
                setIsOpen(false);
                return;
            }

            // Industry standard: Use cached data if available for immediate response
            let data = globalUniversityCache;
            
            if (!data) {
                // Background fetch if cache is empty
                const url = `${API_ENDPOINTS.UNIVERSITIES.LIST}?limit=100`;
                const response = await fetch(url);
                const resJson = await response.json();
                data = resJson.data || [];
                globalUniversityCache = data;
                localStorage.setItem(UNIVERSITY_CACHE.KEY, JSON.stringify({
                    timestamp: Date.now(),
                    universities: data
                }));
            }

            if (data && Array.isArray(data)) {
                const q = trimmed.toLowerCase();
                // When onUnsupportedSearch is provided (home), show major universities even if unsupported.
                // Elsewhere, still filter to supported IDs only when that list is provided.
                const showUnsupportedSuggestions = Boolean(onUnsupportedSearch);
                let filteredData = data.filter((u: University) => u.id !== 0);
                if (
                    supportedUniversityIds &&
                    supportedUniversityIds.length > 0 &&
                    !showUnsupportedSuggestions
                ) {
                    filteredData = filteredData.filter((u: University) => {
                        const universityId = typeof u.id === 'string' ? parseInt(u.id as unknown as string, 10) : u.id;
                        return supportedUniversityIds.includes(universityId);
                    });
                }

                // Query Filtering (prefix/abbreviation/word prefix)
                filteredData = filteredData.filter((u: University) => {
                    const name = (u.name || '').toLowerCase();
                    const abbr = (u.abbreviation || '').toLowerCase();
                    return name.startsWith(q) ||
                        abbr.startsWith(q) ||
                        name.split(' ').some(word => word.startsWith(q)) ||
                        name.includes(q);
                });

                // Sorting — supported first, then priority IDs, then name
                const targetOrder = [10, 123]; // 10=USF, 123=NYU for prioritization
                const sorted = [...filteredData].sort((a: University, b: University) => {
                    const idA = typeof a.id === 'string' ? parseInt(a.id as unknown as string, 10) : a.id;
                    const idB = typeof b.id === 'string' ? parseInt(b.id as unknown as string, 10) : b.id;
                    const supportedA = !supportedUniversityIds?.length || supportedUniversityIds.includes(idA);
                    const supportedB = !supportedUniversityIds?.length || supportedUniversityIds.includes(idB);
                    if (supportedA !== supportedB) return supportedA ? -1 : 1;

                    const indexA = targetOrder.indexOf(idA);
                    const indexB = targetOrder.indexOf(idB);

                    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                    if (indexA !== -1) return -1;
                    if (indexB !== -1) return 1;

                    const countA = a.listing_count ?? 0;
                    const countB = b.listing_count ?? 0;
                    if (countA !== countB) return countB - countA;

                    return (a.name || '').localeCompare(b.name || '');
                }).map((u: University) => {
                    const universityId = typeof u.id === 'string' ? parseInt(u.id as unknown as string, 10) : u.id;
                    const isSupported = !supportedUniversityIds?.length || supportedUniversityIds.includes(universityId);
                    return isSupported ? u : { ...u, unsupported: true };
                });

                // City entries — major US cities when vote flow is available; otherwise supported only
                let cityEntries: University[] = [];
                let stateEntries: University[] = [];
                if (showCities) {
                    stateEntries = matchSupportedStates(trimmed).map((state) => ({
                        id: state.universityId,
                        name: state.label,
                        abbreviation: state.abbreviation,
                        isState: true,
                        cityLabel: state.label,
                    } as University));

                    if (showUnsupportedSuggestions) {
                        cityEntries = matchMajorCities(trimmed).map((city, index) => ({
                            id: city.universityId ?? (-1000 - index),
                            name: city.label,
                            abbreviation: '',
                            isCity: true,
                            cityLabel: city.label,
                            unsupported: !city.supported,
                        } as University));
                    } else {
                        cityEntries = SUPPORTED_CITIES
                            .filter(city => {
                                const normalizedLabel = city.label.toLowerCase().replace(/[^a-z0-9]/g, '');
                                const normalizedQuery = q.replace(/[^a-z0-9]/g, '');
                                const queryWords = q.replace(/[^a-z0-9\s]/g, '').split(/\s+/).filter(Boolean);
                                const cityText = `${city.label} ${city.id} ${(city.aliases || []).join(' ')}`.toLowerCase();

                                if (normalizedLabel.includes(normalizedQuery)) return true;
                                if (queryWords.length > 0 && queryWords.every(word => cityText.includes(word))) return true;
                                if (q.includes('florida') && city.id.endsWith('-fl')) return true;
                                if (
                                    (q.includes('new york') || q === 'ny' || q.endsWith(' ny')) &&
                                    (city.id.endsWith('-ny') || city.id.endsWith('-nj') || city.id.endsWith('-ct'))
                                ) return true;
                                return false;
                            })
                            .map(city => ({
                                id: city.universityId,
                                name: city.label,
                                abbreviation: '',
                                isCity: true,
                                cityLabel: city.label,
                            } as University));
                    }
                }

                // Universities first unless the query exactly matches a location.
                const exactState = matchSupportedStateFromInput(trimmed);
                const exactCity = matchSupportedCityFromInput(trimmed);
                const normalizeLoc = (value: string) =>
                    value.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
                const qNorm = normalizeLoc(trimmed);

                const isExactLocationEntry = (entry: University) => {
                    if (entry.isState) {
                        return !!exactState && entry.id === exactState.universityId;
                    }
                    if (entry.isCity) {
                        if (exactCity && entry.id === exactCity.universityId) return true;
                        // Unsupported / major-city entries: exact on label or city name only
                        const label = normalizeLoc(entry.cityLabel || entry.name);
                        const withoutState = label.replace(/\s+(fl|ny|nj|ct)$/, '').trim();
                        return label === qNorm || withoutState === qNorm;
                    }
                    return false;
                };

                const locationEntries = [...stateEntries, ...cityEntries];
                const exactLocations = locationEntries.filter(isExactLocationEntry);
                const otherLocations = locationEntries.filter((entry) => !isExactLocationEntry(entry));
                const combined = (
                    exactLocations.length > 0
                        ? [...exactLocations, ...sorted, ...otherLocations]
                        : [...sorted, ...locationEntries]
                ).slice(0, 12);
                setSuggestions(combined);
                setIsOpen(combined.length > 0);
            } else {
                setSuggestions([]);
                setIsOpen(false);
            }
        } catch (error) {
            console.error('Error searching universities:', error);
            setSuggestions([]);
            setIsOpen(false);
        }
    }, [supportedUniversityIds, showCities, onUnsupportedSearch]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setSearchQuery(query);
        if (onInputChange) {
            onInputChange(query);
        }

        // Clear previous timeout
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        // Clear selection if user is typing
        if (selectedUniversity && query !== selectedUniversity.name) {
            setSelectedUniversity(null);
            // On non-home pages, signal that the university is being cleared
            if (!isHomePage && onChange) {
                onChange('');
            }
        }

        // Update validation
        if (onValidationChange) {
            const isValid = !query.trim() || (selectedUniversity !== null && query === selectedUniversity.name &&
                (!supportedUniversityIds || supportedUniversityIds.length === 0 || supportedUniversityIds.includes(selectedUniversity.id)));
            onValidationChange(isValid);
        }

        // Debounce search
        if (query.trim()) {
            if (suggestions.length === 0) {
                performSearch(query);
            } else {
                debounceTimeoutRef.current = setTimeout(() => {
                    performSearch(query);
                }, 300);
            }
        } else {
            setIsOpen(false);
            setSuggestions([]);
        }
    };

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
            setUseCompactCapsuleLabel(window.innerWidth <= 1100);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleSuggestionClick = (university: University) => {
        if (university.unsupported) {
            const label = university.isCity || university.isState
                ? (university.cityLabel || university.name)
                : university.name;
            if (onChange) {
                onChange('');
            }
            if (onUnsupportedSearch) {
                onUnsupportedSearch(label);
            }
            setSelectedUniversity(null);
            setIsOpen(false);
            setSuggestions([]);
            setSearchQuery(label);
            if (onInputChange) {
                onInputChange(label);
            }
            if (onValidationChange) {
                onValidationChange(false);
            }
            inputRef.current?.blur();
            return;
        }

        setSelectedUniversity(university);
        setSearchQuery(university.name);
        onChange(university.id.toString());
        if (onInputChange) {
            onInputChange(university.name);
        }
        if (onValidationChange) {
            onValidationChange(true);
        }
        setIsOpen(false);
        setSuggestions([]);
    };

    const handleInputFocus = () => {
        setIsFocused(true);
        // On signup and edit profile pages, don't clear the selected university when focusing
        if (selectedUniversity && !isSignupPage && !isEditProfilePage) {
            setSelectedUniversity(null);
            setSearchQuery('');
            // On non-home pages, signal that the university is being cleared
            if (!isHomePage && onChange) {
                onChange('');
            }
            if (onInputChange) {
                onInputChange('');
            }
            if (onValidationChange) {
                onValidationChange(true);
            }
        }
        // Only open dropdown once the user has typed something
        if (searchQuery.trim()) {
            if (suggestions.length > 0) {
                setIsOpen(true);
            } else {
                performSearch(searchQuery);
            }
        } else {
            setIsOpen(false);
            setSuggestions([]);
        }
    };

    const handleInputClick = () => {
        if (dropdownOnly) {
            const nextOpen = !isOpen;
            setIsOpen(nextOpen);
            if (nextOpen && searchQuery.trim()) {
                performSearch(searchQuery);
            } else if (!nextOpen) {
                setSuggestions([]);
            }
            return;
        }

        setSelectedUniversity(null);
        setSearchQuery('');
        // On non-home pages, signal that the university is being cleared
        if (!isHomePage && onChange) {
            onChange('');
        }
        if (onInputChange) {
            onInputChange('');
        }
        if (onValidationChange) {
            onValidationChange(true);
        }
        setIsOpen(false);
        setSuggestions([]);
        inputRef.current?.focus();
    };

    const handleSearchIconClick = () => {
        if (dropdownOnly) {
            handleInputClick();
            return;
        }
        setSelectedUniversity(null);
        setSearchQuery('');
        onChange('');
        if (onInputChange) {
            onInputChange('');
        }
        if (onValidationChange) {
            onValidationChange(true);
        }
        inputRef.current?.focus();
    };

    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setTimeout(() => {
            if (!searchRef.current?.contains(document.activeElement)) {
                setIsOpen(false);
                setIsFocused(false);

                // Enforce valid selection on non-home pages (can't just write whatever)
                if (!isHomePage) {
                    if (!selectedUniversity) {
                        setSearchQuery('');
                        if (onInputChange) {
                            onInputChange('');
                        }
                    } else {
                        setSearchQuery(selectedUniversity.name);
                    }
                }
            }
        }, 150);
    };

    const handleSuggestionMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (suggestions.length > 0) {
                handleSuggestionClick(suggestions[0]);
            } else if (searchQuery.trim()) {
                if (onUnsupportedSearch) {
                    onUnsupportedSearch(searchQuery);
                }
                inputRef.current?.blur();
            }
        } else if (e.key === 'Escape') {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const displayValue = selectedUniversity 
        ? (isMobile && selectedUniversity.abbreviation ? selectedUniversity.abbreviation : selectedUniversity.name) 
        : searchQuery;

    /**
     * Homescreen empty-state copy: use the long hint only when it fully fits.
     * Otherwise keep "Search" — never mid-word truncate. Search icon carries the affordance.
     */
    const adaptivePlaceholder = useMemo(() => {
        const longCopy = placeholder || 'Search University or City';
        const shortCopy = 'Search';

        if (!isHomePage) {
            return longCopy;
        }

        const width = containerWidth;
        if (width == null) {
            if (typeof window !== 'undefined' && window.innerWidth <= 1100) return shortCopy;
            return longCopy;
        }

        const canvas = measureCanvasRef.current ?? document.createElement('canvas');
        measureCanvasRef.current = canvas;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return width < 280 ? shortCopy : longCopy;
        }

        const inputEl = inputRef.current;
        const computed = inputEl ? window.getComputedStyle(inputEl) : null;
        const baseFontSizeToken = computed?.getPropertyValue('--home-search-font-size')?.trim() || '';
        const parsedBase = baseFontSizeToken ? parseFloat(baseFontSizeToken) : NaN;
        const fallbackBase = window.matchMedia('(max-width: 768px)').matches ? 18 : 22;
        const baseFontSize = Math.max(1, Number.isFinite(parsedBase) && parsedBase > 0 ? parsedBase : fallbackBase);
        const fontStyle = computed?.fontStyle || 'normal';
        const fontWeight = computed?.fontWeight || '400';
        const fontFamily = computed?.fontFamily || '"DM Mono", monospace';
        const letterSpacing = computed?.letterSpacing === 'normal'
            ? 0
            : parseFloat(computed?.letterSpacing || '0') || 0;

        ctx.font = `${fontStyle} ${fontWeight} ${baseFontSize}px ${fontFamily}`;
        const textWidth = ctx.measureText(longCopy).width + Math.max(0, longCopy.length - 1) * letterSpacing;
        const effectiveWidth = width * 0.92;

        return textWidth <= effectiveWidth ? longCopy : shortCopy;
    }, [containerWidth, placeholder, isHomePage, fontReadyTick]);

    const getAutocompleteHint = () => {
        if (!isHomePage || selectedUniversity || !suggestions.length || !searchQuery.trim()) {
            return '';
        }
        const firstSuggestion = suggestions[0];
        const queryLower = searchQuery.toLowerCase();
        const suggestionLower = firstSuggestion.name.toLowerCase();

        if (suggestionLower.startsWith(queryLower)) {
            return firstSuggestion.name.substring(searchQuery.length);
        }
        return '';
    };

    const autocompleteHint = getAutocompleteHint();

    useEffect(() => {
        if (!isHomePage || !inputContainerRef.current) {
            setContainerWidth(null);
            return;
        }

        const el = inputContainerRef.current;

        if (typeof ResizeObserver !== 'undefined') {
            const ro = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    if (entry.target === el) {
                        setContainerWidth(entry.contentRect.width);
                    }
                }
            });
            ro.observe(el);
            setContainerWidth(el.getBoundingClientRect().width);
            return () => ro.disconnect();
        }

        const handleResize = () => {
            setContainerWidth(el.getBoundingClientRect().width);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isHomePage]);

    useEffect(() => {
        if (!isHomePage) {
            if (dynamicFontSize !== null) setDynamicFontSize(null);
            return;
        }

        const availableWidth = containerWidth ?? 0;
        if (!availableWidth) {
            if (dynamicFontSize !== null) setDynamicFontSize(null);
            return;
        }

        const paddingFactor = 0.92;
        const effectiveWidth = availableWidth * paddingFactor;

        const textToMeasure =
            autocompleteHint && searchQuery
                ? searchQuery + autocompleteHint
                : displayValue || adaptivePlaceholder || '';

        if (!textToMeasure) {
            if (dynamicFontSize !== null) setDynamicFontSize(null);
            return;
        }

        const inputEl = inputRef.current;
        const computed = inputEl ? window.getComputedStyle(inputEl) : null;
        // Base size from CSS var only — do not use the current computed font-size,
        // which may still be the shrunk inline value from a long query.
        const baseFontSizeToken = computed?.getPropertyValue('--home-search-font-size')?.trim() || '';
        const parsedBase = baseFontSizeToken ? parseFloat(baseFontSizeToken) : NaN;
        const fallbackBase = window.matchMedia('(max-width: 768px)').matches ? 18 : 22;
        const baseFontSize = Math.max(1, Number.isFinite(parsedBase) && parsedBase > 0 ? parsedBase : fallbackBase);
        // Placeholders stay at full size (we shorten the copy instead of squishing).
        // Typed text may shrink a bit to fit.
        const isPlaceholderOnly = !displayValue && !searchQuery;
        if (isPlaceholderOnly) {
            setDynamicFontSize((prev) => (prev === null ? prev : null));
            return;
        }
        const minFontSize = 12;

        const canvas = measureCanvasRef.current ?? document.createElement('canvas');
        measureCanvasRef.current = canvas;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            if (dynamicFontSize !== null) setDynamicFontSize(null);
            return;
        }

        const fontStyle = computed?.fontStyle || 'normal';
        const fontWeight = computed?.fontWeight || '400';
        const fontFamily = computed?.fontFamily || '"DM Mono", monospace';
        const letterSpacing = computed?.letterSpacing === 'normal'
            ? 0
            : parseFloat(computed?.letterSpacing || '0') || 0;

        ctx.font = `${fontStyle} ${fontWeight} ${baseFontSize}px ${fontFamily}`;
        const textWidth = ctx.measureText(textToMeasure).width + Math.max(0, textToMeasure.length - 1) * letterSpacing;

        if (textWidth <= effectiveWidth) {
            // Drop inline override so the CSS default size is restored
            setDynamicFontSize((prev) => (prev === null ? prev : null));
            return;
        }

        const scaleFactor = effectiveWidth / textWidth;
        const nextFontSize = Math.max(minFontSize, Math.floor(baseFontSize * scaleFactor));

        setDynamicFontSize((prev) => (prev === nextFontSize ? prev : nextFontSize));
    }, [displayValue, autocompleteHint, searchQuery, isHomePage, containerWidth, fontReadyTick, className, dynamicFontSize, adaptivePlaceholder]);

    const homeDynamicInputStyle: React.CSSProperties | undefined =
        dynamicFontSize !== null
            ? ({
                  fontSize: `${dynamicFontSize}px`,
              } as React.CSSProperties)
            : undefined;

    const selectedFullLabel = selectedUniversity
        ? (selectedUniversity.isCity || selectedUniversity.isState
            ? (selectedUniversity.cityLabel || selectedUniversity.name)
            : selectedUniversity.name)
        : (dropdownOnly ? 'All' : 'Select University');

    /** Browse capsule: short city name, state name, or uni abbr/full name by width. */
    const selectedCapsuleLabel = (() => {
        if (!selectedUniversity) return selectedFullLabel;
        if (selectedUniversity.isState) {
            return selectedUniversity.cityLabel || selectedUniversity.name;
        }
        if (selectedUniversity.isCity) {
            return shortCityLabel(selectedUniversity.cityLabel || selectedUniversity.name);
        }
        if (useCompactCapsuleLabel && selectedUniversity.abbreviation) {
            return selectedUniversity.abbreviation;
        }
        return selectedUniversity.name;
    })();

    const selectedUniversityLogo =
        selectedUniversity && !selectedUniversity.isCity && !selectedUniversity.isState
            ? getUniversityLogo(selectedUniversity.name, selectedUniversity.abbreviation)
            : undefined;

    const showBrowseCapsule =
        isBrowsePageSearch && !!selectedUniversity && !isFocused && !disableCapsule;

    const searchIconSvg = (size: number) => (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ display: 'block' }}
            aria-hidden="true"
        >
            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    return (
        <div
            className={`university-search ${className} ${dropdownOnly ? 'university-search--dropdown-only' : ''}`}
            ref={searchRef}
        >
            {isHomePage ? (
                <div className="university-search-wrapper">
                    {label && <label className="university-search-label">{label}</label>}
                    <div className="university-search-input-wrapper">
                        {!dropdownOnly && (
                            <div
                                className="university-search-icon"
                                onClick={handleInputClick}
                                style={{ cursor: 'pointer' }}
                            >
                                {searchIconSvg(28)}
                            </div>
                        )}
                        <div className="university-search-input-container" ref={inputContainerRef}>
                            {autocompleteHint && isHomePage && (
                                <span
                                    className="university-search-autocomplete-hint"
                                    style={homeDynamicInputStyle}
                                >
                                    <span className="autocomplete-hint-typed">{searchQuery}</span>
                                    <span className="autocomplete-hint-text">{autocompleteHint}</span>
                                </span>
                            )}
                            <input
                                ref={inputRef}
                                type="text"
                                className={`university-search-input ${(selectedUniversity && !isFocused && !disableCapsule) || dropdownOnly ? 'hidden-input' : ''}`}
                                value={displayValue}
                                onChange={handleInputChange}
                                onFocus={handleInputFocus}
                                onClick={handleInputClick}
                                onBlur={handleInputBlur}
                                onKeyDown={handleKeyDown}
                                placeholder={adaptivePlaceholder}
                                aria-label="Search University or City"
                                maxLength={75}
                                style={homeDynamicInputStyle}
                                readOnly={dropdownOnly}
                            />
                            {(selectedUniversity || dropdownOnly) && !isFocused && !disableCapsule && (
                                showBrowseCapsule ? (
                                    <div
                                        className={`selected-university-capsule browse-location-capsule ${selectedUniversity.isCity ? 'is-city' : ''} ${selectedUniversity.isState ? 'is-state' : ''} ${!selectedUniversity.isCity && !selectedUniversity.isState ? 'is-university' : ''} ${selectedUniversityLogo ? 'has-logo' : 'no-logo'}`}
                                        onClick={handleInputClick}
                                        title={selectedFullLabel}
                                        aria-label={`Search location: ${selectedFullLabel}`}
                                        role="button"
                                    >
                                        {selectedUniversityLogo ? (
                                            <div className="capsule-logo-container">
                                                <img
                                                    src={selectedUniversityLogo}
                                                    alt=""
                                                    className="capsule-logo"
                                                />
                                            </div>
                                        ) : null}
                                        <span className="capsule-text">
                                            {selectedCapsuleLabel}
                                        </span>
                                    </div>
                                ) : (
                                    <div
                                        className={`selected-university-capsule ${dropdownOnly ? 'dropdown-mode' : ''} ${selectedUniversity && getUniversityLogo(selectedUniversity.name, selectedUniversity.abbreviation) ? 'has-logo' : 'no-logo'}`}
                                        onClick={handleInputClick}
                                    >
                                        {selectedUniversity && !selectedUniversity.isCity && !selectedUniversity.isState && getUniversityLogo(selectedUniversity.name, selectedUniversity.abbreviation) ? (
                                            <div className="capsule-logo-container">
                                                <img
                                                    src={getUniversityLogo(selectedUniversity.name, selectedUniversity.abbreviation)}
                                                    alt={selectedUniversity.abbreviation}
                                                    className="capsule-logo"
                                                />
                                            </div>
                                        ) : null}
                                        <span className="capsule-text">
                                            {selectedUniversity
                                                ? (selectedUniversity.isCity
                                                    ? shortCityLabel(selectedUniversity.cityLabel || selectedUniversity.name)
                                                    : selectedUniversity.isState
                                                        ? (selectedUniversity.cityLabel || selectedUniversity.name)
                                                        : (isMobile && selectedUniversity.abbreviation ? selectedUniversity.abbreviation : selectedUniversity.name))
                                                : (dropdownOnly ? 'All' : 'Select University')}
                                        </span>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {label && <label className="university-search-label">{label}</label>}
                    <div className="university-search-input-wrapper">
                        <div
                            className="university-search-icon"
                            onClick={handleSearchIconClick}
                            style={{ cursor: 'pointer' }}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <input
                            ref={inputRef}
                            type="text"
                            className={`university-search-input ${(selectedUniversity && !isFocused && !disableCapsule && !isSignupPage) || dropdownOnly ? 'hidden-input' : ''}`}
                            value={displayValue}
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            onClick={handleInputClick}
                            onBlur={handleInputBlur}
                            onKeyDown={handleKeyDown}
                            placeholder={adaptivePlaceholder}
                            aria-label={placeholder || 'Search university'}
                            maxLength={75}
                            readOnly={dropdownOnly}
                        />
                        {(selectedUniversity || dropdownOnly) && !isFocused && !disableCapsule && !isSignupPage && (
                            <div
                                className={`selected-university-capsule ${dropdownOnly ? 'dropdown-mode' : ''} ${selectedUniversity && getUniversityLogo(selectedUniversity.name, selectedUniversity.abbreviation) ? 'has-logo' : 'no-logo'}`}
                                onClick={handleInputClick}
                            >
                                {selectedUniversity && getUniversityLogo(selectedUniversity.name, selectedUniversity.abbreviation) ? (
                                    <div className="capsule-logo-container">
                                        <img
                                            src={getUniversityLogo(selectedUniversity.name, selectedUniversity.abbreviation)}
                                            alt={selectedUniversity.abbreviation}
                                            className="capsule-logo"
                                        />
                                    </div>
                                ) : null}
                                <span className="capsule-text">
                                    {selectedUniversity 
                                        ? (isMobile && selectedUniversity.abbreviation ? selectedUniversity.abbreviation : selectedUniversity.name) 
                                        : (dropdownOnly ? "All" : "Select University")}
                                </span>
                            </div>
                        )}
                    </div>
                </>
            )}

            {isOpen && suggestions.length > 0 && (
                <div ref={dropdownRef} className="university-search-dropdown">
                    {suggestions.map((university) => (
                        <div
                            key={`${university.isState ? 'state' : university.isCity ? 'city' : 'uni'}-${university.id}`}
                            className={`university-search-option ${selectedUniversity?.id === university.id && !university.isCity && !university.isState ? 'selected' : ''}`}
                            onClick={() => handleSuggestionClick(university)}
                            onMouseDown={handleSuggestionMouseDown}
                        >
                            <div className="university-search-option-content">
                                {!university.isCity && !university.isState && isHomePage && (
                                    <div className="university-search-option-logo-container">
                                        {getUniversityLogo(university.name, university.abbreviation) ? (
                                            <img
                                                src={getUniversityLogo(university.name, university.abbreviation)}
                                                alt={university.abbreviation || university.name}
                                                className="university-search-option-logo"
                                            />
                                        ) : (
                                            <div className="university-search-option-logo-fallback">
                                                {(university.abbreviation || university.name).charAt(0)}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="university-search-option-info">
                                    <div className="university-search-option-name">
                                        {university.isCity || university.isState
                                            ? (university.cityLabel || university.name)
                                            : university.name}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default UniversitySearch;
